import { Controller, FormProvider, useForm } from 'react-hook-form';

import {
  basicInfoSchema,
  type BasicInfoFormValues,
} from '~domains/profile/entities/person';

import { ampli } from '~entities/ampli';

import { useOnMountEffect } from '~shared/hooks/use-on-mount-effect';
import { FooterWithProgressBar } from '~shared/ui/footer-with-progress-bar';
import { Screen, ScreenHeader } from '~shared/ui/screen';

import { TextField } from '@breezeventures/ui-kit/text-field/rhf';
import { Typography } from '@breezeventures/ui-kit/typography';
import { InputDate } from '@breezeventures/web-ph-components/input-date';
import { zodResolver } from '@hookform/resolvers/zod';

type BasicInfoProps = {
  initialValues?: Partial<BasicInfoFormValues>;
  onBack?: () => void;
  onSubmit: (values: BasicInfoFormValues) => void;
};

export const BasicInfo = ({
  initialValues,
  onBack,
  onSubmit,
}: BasicInfoProps) => {
  const methods = useForm<BasicInfoFormValues>({
    resolver: zodResolver(basicInfoSchema),
    mode: 'all',
    defaultValues: initialValues ?? {
      firstName: '',
      middleName: '',
      lastName: '',
      dateOfBirth: '',
      email: '',
      pointOfContact: '',
    },
  });

  useOnMountEffect(() => {
    ampli.client.track('mp_profile_basic_info_screen_shown');
  });

  return (
    <form
      onSubmit={methods.handleSubmit(onSubmit)}
      className="flex h-full flex-col"
    >
      <Screen
        header={<ScreenHeader onBack={onBack} />}
        footer={
          <FooterWithProgressBar
            totalSteps={4}
            currentStepIndex={0}
            submitButtonText="Next"
            isLoading={methods.formState.isSubmitting}
          />
        }
      >
        <FormProvider {...methods}>
          <Typography.Title size="s" className="py-6">
            Add your basic info
          </Typography.Title>

          <div className="flex flex-col gap-3">
            <Controller
              name="firstName"
              control={methods.control}
              render={(fieldProps) => (
                <TextField
                  {...fieldProps}
                  label="First name"
                  labelPosition="inner"
                />
              )}
            />

            <Controller
              name="middleName"
              control={methods.control}
              render={(fieldProps) => (
                <TextField
                  {...fieldProps}
                  label="Middle name"
                  labelPosition="inner"
                />
              )}
            />

            <Controller
              name="lastName"
              control={methods.control}
              render={(fieldProps) => (
                <TextField
                  {...fieldProps}
                  label="Last name"
                  labelPosition="inner"
                />
              )}
            />

            <Controller
              name="dateOfBirth"
              control={methods.control}
              render={(fieldProps) => (
                <InputDate {...fieldProps} label="Date of birth" />
              )}
            />

            <Controller
              name="email"
              control={methods.control}
              render={(fieldProps) => (
                <TextField
                  {...fieldProps}
                  label="E-mail"
                  labelPosition="inner"
                  inputMode="email"
                />
              )}
            />
          </div>

          <div className="mt-6 flex flex-col gap-3">
            <Typography.Text size="m" weight="medium">
              Who&apos;s your main contact at Skyro?
            </Typography.Text>
            <Controller
              name="pointOfContact"
              control={methods.control}
              render={(fieldProps) => (
                <TextField
                  {...fieldProps}
                  labelPosition="inner"
                  placeholder="Skyro account/BD manager (if any)"
                />
              )}
            />
          </div>
        </FormProvider>
      </Screen>
    </form>
  );
};
